import { type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { colors } from '../../tokens/colors';
import { borderRadius } from '../../tokens/spacing';

/* ── Types ── */

/**
 * `processing` e `critical` vem dos "Integration/Automation Status Tags" do
 * DESIGN.md: processamento em andamento pulsa, e falha critica e SOLIDA com
 * texto branco (nao um tom claro), para saltar aos olhos numa tela densa —
 * transmissao a SEFAZ que falhou nao pode passar batida.
 */
type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'processing'
  | 'critical';
type BadgeSize = 'sm' | 'md';
type BadgeAppearance = 'pill' | 'dot' | 'icon';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  appearance?: BadgeAppearance;
  dot?: boolean;
  icon?: ReactNode;
  className?: string;
}

/* ── Theme tokens ── */

const variantTokens: Record<BadgeVariant, {
  '--badge-bg': string;
  '--badge-color': string;
  '--badge-border': string;
  '--badge-dot': string;
}> = {
  default: {
    '--badge-bg': colors.neutral[50],
    '--badge-color': colors.neutral[600],
    '--badge-border': colors.border.default,
    '--badge-dot': colors.neutral[500],
  },
  success: {
    '--badge-bg': colors.success[50],
    '--badge-color': colors.success[700],
    '--badge-border': colors.success[200],
    '--badge-dot': colors.success[500],
  },
  warning: {
    '--badge-bg': colors.warning[50],
    '--badge-color': colors.warning[700],
    '--badge-border': colors.warning[200],
    '--badge-dot': colors.warning[500],
  },
  error: {
    '--badge-bg': colors.error[50],
    '--badge-color': colors.error[700],
    '--badge-border': colors.error[200],
    '--badge-dot': colors.error[500],
  },
  info: {
    '--badge-bg': colors.info[50],
    '--badge-color': colors.info[700],
    '--badge-border': colors.info[200],
    '--badge-dot': colors.info[500],
  },
  neutral: {
    '--badge-bg': colors.neutral[100],
    '--badge-color': colors.neutral[700],
    '--badge-border': colors.border.default,
    '--badge-dot': colors.neutral[500],
  },
  processing: {
    '--badge-bg': colors.info[50],
    '--badge-color': colors.info[700],
    '--badge-border': colors.info[200],
    '--badge-dot': colors.info[500],
  },
  critical: {
    '--badge-bg': colors.error[500],
    '--badge-color': '#ffffff',
    '--badge-border': colors.error[500],
    '--badge-dot': '#ffffff',
  },
};

const sizeTokens: Record<BadgeSize, { padding: string; fontSize: string; gap: string; dotSize: string }> = {
  sm: { padding: '0.0625rem 0.375rem', fontSize: '0.6875rem', gap: '0.25rem', dotSize: '5px' },
  md: { padding: '0.125rem 0.5rem', fontSize: '0.75rem', gap: '0.3125rem', dotSize: '6px' },
};

/* ── Component ── */

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  appearance = 'pill',
  dot = false,
  icon,
  className,
}: BadgeProps): JSX.Element {
  const vToken = variantTokens[variant];
  const sToken = sizeTokens[size];

  return (
    <span
      className={cn(
        'ds-badge',
        `ds-badge--${variant}`,
        `ds-badge--${size}`,
        `ds-badge--${appearance}`,
        className,
      )}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sToken.gap,
        padding: sToken.padding,
        fontSize: sToken.fontSize,
        fontWeight: 700,
        lineHeight: 1,
        borderRadius: appearance === 'icon' ? borderRadius.md : borderRadius.full,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        fontVariantNumeric: 'tabular-nums',
        ...vToken,
        /*
          As tres variaveis abaixo eram DECLARADAS por `vToken` e nunca
          consumidas: nao havia `background`, `color` nem `border` no estilo,
          e nenhuma regra `.ds-badge` no styles.css os lia. Resultado: todo
          Badge da aplicacao renderizava como texto pelado — sem preenchimento,
          sem borda e herdando a cor do texto ao redor. So o ponto (`--badge-dot`)
          aparecia.

          Encontrado ao renderizar /finance/payables e comparar com a tela de
          referencia, onde a tag de status e um container preenchido.
        */
        background: 'var(--badge-bg)',
        color: 'var(--badge-color)',
        border: '1px solid var(--badge-border)',
      }}
    >
      {(dot || appearance === 'dot') && (
        <span
          className="ds-badge__dot"
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: sToken.dotSize,
            height: sToken.dotSize,
            borderRadius: '50%',
            background: 'var(--badge-dot)',
            flexShrink: 0,
          }}
        />
      )}
      {icon && (
        <span className="ds-badge__icon" aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}

Badge.displayName = 'Badge';
